import React from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { useToast } from '../hooks/use-toast';
import { Loader2, ArrowRight } from 'lucide-react';
import { countryCodes } from '../data/mockData';
import { useState } from 'react';

const ContactFormDialog = ({ isOpen, onClose }) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', countryCode: '+1', phone: '', description: ''
  });
  const [errors, setErrors] = useState({ email: '', phone: '' });

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'email' || field === 'phone') setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handlePhoneChange = (value) => {
    const digitsOnly = value.replace(/\D/g, '');
    if (digitsOnly.length <= 10) {
      handleChange('phone', digitsOnly);
      if (digitsOnly && digitsOnly.length !== 10) {
        setErrors(prev => ({ ...prev, phone: 'Phone number must be exactly 10 digits' }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = { email: '', phone: '' };
    let hasErrors = false;
    if (!validateEmail(formData.email)) { newErrors.email = 'Please enter a valid email address'; hasErrors = true; }
    if (formData.phone.length !== 10) { newErrors.phone = 'Phone number must be exactly 10 digits'; hasErrors = true; }
    if (hasErrors) { setErrors(newErrors); return; }

    setIsSubmitting(true);
    setTimeout(() => {
      toast({ title: "Request Submitted!", description: "Our trademark experts will contact you within 24 hours." });
      setIsSubmitting(false);
      setFormData({ name: '', email: '', countryCode: '+1', phone: '', description: '' });
      setErrors({ email: '', phone: '' });
      onClose();
    }, 1500);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent data-testid="contact-form-dialog" className="max-w-[900px] p-0 gap-0 overflow-hidden border border-amber-500/30 bg-[#030308] rounded-xl">
        <div className="flex flex-col md:flex-row">
          {/* Left - Image */}
          <div className="hidden md:block w-[40%] relative">
            <img
              src="https://images.unsplash.com/photo-1774891937400-b5e32f719fae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHx0cmFkZW1hcmslMjByZWdpc3RyYXRpb24lMjBkb2N1bWVudCUyMG9mZmljaWFsJTIwc3RhbXAlMjBzZWFsfGVufDB8fHx8MTc3NTQ5NjM2M3ww&ixlib=rb-4.1.0&q=85"
              alt="Trademark registration stamp"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#030308]/30" />
          </div>

          {/* Right - Form */}
          <div className="w-full md:w-[60%] p-7 md:p-8">
            <div className="mb-5">
              <span className="inline-block bg-gradient-to-r from-amber-500 to-orange-600 text-white px-3 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider mb-3">Free Consultation</span>
              <h2 className="text-xl font-bold text-white mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Don't Wait. Protect Your Brand Now.</h2>
              <p className="text-sm text-slate-400">A trademark specialist will connect with you within 24 hours.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3.5">
              <div className="space-y-1.5">
                <Label htmlFor="dialog-name" className="text-slate-400 text-sm">Full Name *</Label>
                <Input id="dialog-name" data-testid="dialog-name-input" placeholder="Enter your full name" value={formData.name} onChange={(e) => handleChange('name', e.target.value)} required className="h-10 bg-white/5 border-white/10 text-white placeholder:text-slate-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 rounded-lg" />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="dialog-email" className="text-slate-400 text-sm">Email *</Label>
                <Input id="dialog-email" data-testid="dialog-email-input" type="email" placeholder="your@email.com" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} required className={`h-10 bg-white/5 text-white placeholder:text-slate-600 rounded-lg ${errors.email ? 'border-red-500' : 'border-white/10'} focus:border-amber-500 focus:ring-1 focus:ring-amber-500`} />
                {errors.email && <p className="text-red-400 text-xs">{errors.email}</p>}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="dialog-phone" className="text-slate-400 text-sm">Phone *</Label>
                <div className="flex gap-2">
                  <Select value={formData.countryCode} onValueChange={(v) => handleChange('countryCode', v)}>
                    <SelectTrigger data-testid="dialog-country-select" className="w-[110px] h-10 bg-white/5 border-white/10 text-white focus:border-amber-500 rounded-lg">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px] bg-[#0A0A12] border-white/10">
                      {countryCodes.map((item) => (
                        <SelectItem key={item.code} value={item.code} className="text-white hover:bg-white/5 focus:bg-white/5 focus:text-white">
                          <span className="flex items-center gap-2"><span>{item.flag}</span><span>{item.code}</span></span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input id="dialog-phone" data-testid="dialog-phone-input" type="tel" placeholder="1234567890" value={formData.phone} onChange={(e) => handlePhoneChange(e.target.value)} required maxLength={10} className={`flex-1 h-10 bg-white/5 text-white placeholder:text-slate-600 rounded-lg ${errors.phone ? 'border-red-500' : 'border-white/10'} focus:border-amber-500 focus:ring-1 focus:ring-amber-500`} />
                </div>
                {errors.phone && <p className="text-red-400 text-xs">{errors.phone}</p>}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="dialog-desc" className="text-slate-400 text-sm">What do you need?</Label>
                <Textarea id="dialog-desc" data-testid="dialog-description-input" placeholder="e.g. Register my brand for Amazon Brand Registry" rows={2} value={formData.description} onChange={(e) => handleChange('description', e.target.value)} className="bg-white/5 border-white/10 text-white placeholder:text-slate-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 resize-none rounded-lg" />
              </div>

              <Button type="submit" data-testid="dialog-submit-button" className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-bold h-10 text-sm rounded-md uppercase tracking-wider btn-shine shadow-lg shadow-amber-500/20" disabled={isSubmitting}>
                {isSubmitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</>) : (<>Submit Request <ArrowRight className="ml-2 w-4 h-4" /></>)}
              </Button>

              <p className="text-[10px] text-slate-600 text-center">By submitting, you agree to our Terms and Privacy Policy.</p>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactFormDialog;
